import { ScaleElement } from '@scale/base-element';
import { CartAPI } from '@scale/cart-api';
import { Events, VariantSelectedEvent } from '@scale/events';
import { formatMoney } from '@scale/currency';
import { getCartSections } from '@scale/utilities';
import { mergeItemsById } from '@scale/bundle-utils';

const HAS_BUNDLE = '[data-bundle-contributor]';

class ProductForm extends ScaleElement {
  #submitting = false;
  #stickyObserver;
  #currentVariant;

  setup() {
    const form = this.querySelector('form');
    if (form) form.addEventListener('submit', (e) => this.#handleSubmit(e), { signal: this.signal });

    this.#currentVariant = this.#initialVariant();

    this.onDocument(Events.VARIANT_SELECTED, (e) => this.#onVariantSelected(e.variant));
    this.onDocument(Events.BUNDLE_UPDATED, () => this.#refreshAtcPrice());
    this.onDocument(Events.QUANTITY_BREAKS_UPDATED, () => this.#refreshAtcPrice());

    this.#setupStickyATC();
    this.#setupNotifyForm();

    // Reflect the bundle total on first paint, once sibling bundle elements
    // have run their own setup and populated their item datasets.
    requestAnimationFrame(() => this.#refreshAtcPrice());
  }

  /** Variant matching the form's initial `id` input, or null if it isn't in the variants JSON. */
  #initialVariant() {
    const id = this.ref('variant-id')?.value;
    return this.#variants().find((v) => String(v.id) === String(id)) ?? null;
  }

  #variants() {
    const json = this.querySelector('[data-product-variants], [data-ref="variants-json"]');
    if (!json) return [];
    try {
      return JSON.parse(json.textContent);
    } catch {
      return [];
    }
  }

  async #handleSubmit(e) {
    e.preventDefault();
    if (this.#submitting) return;

    const form = e.target;
    const errorEl = this.ref('error');
    const errorMsg = this.ref('error-message');
    if (errorEl) errorEl.hidden = true;

    const variantInput = this.ref('variant-id');
    if (!variantInput || variantInput.disabled) return;
    if (this.querySelector('[data-bundle-contributor][data-bundle-blocked="true"]')) return;

    const formData = new FormData(form);
    let items = [{
      id: Number(formData.get('id')),
      quantity: Number(formData.get('quantity')) || 1,
      properties: this.#extractProperties(formData),
    }];

    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) items[0].selling_plan = Number(sellingPlan);

    items = this.#applyBundleContributions(items);

    this.#submitting = true;
    const addButton = this.ref('add-to-cart');
    if (addButton) addButton.setAttribute('aria-busy', 'true');

    const text = this.ref('text');
    const loading = this.ref('loading');
    const check = this.ref('check');
    const status = this.ref('status');

    if (text) text.hidden = true;
    if (loading) loading.hidden = false;

    try {
      const cart = await CartAPI.add(items, {
        sections: getCartSections(),
      });

      if (loading) loading.hidden = true;
      if (check) check.hidden = false;
      if (status) status.textContent = window.Scale?.translations?.addedToCart || 'Added to cart';
      this.announce(window.Scale?.translations?.addedToCart || 'Added to cart');

      setTimeout(() => {
        if (check) check.hidden = true;
        if (text) text.hidden = false;
      }, 1500);
    } catch (err) {
      if (loading) loading.hidden = true;
      if (text) text.hidden = false;
      if (errorEl && errorMsg) {
        errorMsg.textContent = err.message;
        errorEl.hidden = false;
      }
      if (status) status.textContent = err.message;
    } finally {
      this.#submitting = false;
      if (addButton) addButton.removeAttribute('aria-busy');
    }
  }

  #onVariantSelected(variant) {
    this.#currentVariant = variant;

    const addButton = this.ref('add-to-cart');
    const label = this.ref('atc-label');
    const variantInput = this.ref('variant-id');
    const status = this.ref('status');

    if (variantInput) {
      variantInput.value = variant.id;
      variantInput.disabled = !variant.available;
    }

    if (addButton) {
      addButton.disabled = !variant.available;
      addButton.dataset.variantAvailable = String(variant.available);
    }

    const labelText = variant.available
      ? window.Scale?.translations?.addToCart || 'Add to cart'
      : window.Scale?.translations?.soldOut || 'Sold out';

    if (label) label.textContent = labelText;

    if (status) {
      status.textContent = variant.available
        ? `${labelText} ${formatMoney(variant.price)}`
        : labelText;
    }

    this.#refreshAtcPrice();
    this.#updateStickyATC(variant);
    this.#toggleNotify(variant);
  }

  /**
   * Set the ATC price label. With bundle contributors the displayed total is the
   * (discounted) bundle total — replacing the variant price when a contributor
   * replaces it, otherwise added on top. No contributors: just the variant price.
   */
  #refreshAtcPrice() {
    const atcPrice = this.ref('atc-price');
    const variant = this.#currentVariant;
    if (!atcPrice || !variant) return;

    if (!variant.available) {
      atcPrice.textContent = '';
      return;
    }

    let total = variant.price;
    for (const el of this.querySelectorAll(HAS_BUNDLE)) {
      const contribution = el.getContributionTotal?.();
      if (!contribution) continue;
      total = contribution.replace ? contribution.total : total + contribution.total;
    }

    atcPrice.textContent = `— ${formatMoney(total)}`;
  }

  /**
   * Gather getCartContribution() results from [data-bundle-contributor] descendants.
   * A contribution with replace=true overrides the form's main item (preserving form-level
   * properties and selling_plan on contributed items).
   */
  #applyBundleContributions(items) {
    const contributors = this.querySelectorAll('[data-bundle-contributor]');
    if (!contributors.length) return items;

    const baseProps = items[0]?.properties;
    const baseSellingPlan = items[0]?.selling_plan;
    let result = [...items];

    for (const el of contributors) {
      const contrib = el.getCartContribution?.();
      if (!contrib?.items?.length) continue;

      if (contrib.replace) {
        result = contrib.items.map((it) => ({
          ...it,
          properties: baseProps ? { ...baseProps, ...(it.properties ?? {}) } : it.properties,
          selling_plan: it.selling_plan ?? baseSellingPlan,
        }));
      } else {
        result.push(...contrib.items);
      }
    }

    return mergeItemsById(result);
  }

  #extractProperties(formData) {
    const props = {};
    for (const [key, value] of formData.entries()) {
      if (key.startsWith('properties[') && value) {
        props[key.replace('properties[', '').replace(']', '')] = value;
      }
    }
    return Object.keys(props).length ? props : undefined;
  }

  // --- Sticky ATC ---

  #setupStickyATC() {
    const stickyBar = this.ref('sticky-bar');
    const addButton = this.ref('add-to-cart');
    if (!stickyBar || !addButton) return;

    stickyBar.hidden = false;

    this.#stickyObserver = new IntersectionObserver(
      ([entry]) => {
        // Only show sticky when scrolled BELOW the button (button is above viewport)
        const isAboveViewport = entry.boundingClientRect.bottom < 0;
        stickyBar.classList.toggle('is-visible', !entry.isIntersecting && isAboveViewport);
      },
      { threshold: 0 }
    );
    this.#stickyObserver.observe(addButton);

    const stickySubmit = this.ref('sticky-submit');
    if (stickySubmit) {
      stickySubmit.addEventListener('click', () => {
        const form = this.querySelector('form');
        if (form) form.requestSubmit();
      }, { signal: this.signal });
    }

    // Variant select in sticky bar — sync with main variant picker
    const stickyVariantSelect = this.ref('sticky-variant-select');
    if (stickyVariantSelect) {
      stickyVariantSelect.addEventListener('change', (e) => {
        const variantId = e.target.value;
        // Update the main variant input
        const variantInput = this.ref('variant-id');
        if (variantInput) variantInput.value = variantId;

        // Find variant data and broadcast event
        const variant = this.#variants().find((v) => String(v.id) === String(variantId));
        if (variant) this.broadcast(new VariantSelectedEvent(variant));
      }, { signal: this.signal });
    }
  }

  #updateStickyATC(variant) {
    const stickyBar = this.ref('sticky-bar');
    if (!stickyBar) return;

    const stickyVariant = this.ref('sticky-variant');
    const stickyCurrentPrice = this.ref('sticky-current-price');
    const stickyPrice = this.ref('sticky-price');
    const stickySubmit = this.ref('sticky-submit');

    if (stickyVariant) stickyVariant.textContent = variant.title;
    if (stickyCurrentPrice) stickyCurrentPrice.textContent = formatMoney(variant.price);

    // Sync sticky variant select
    const stickyVariantSelect = this.ref('sticky-variant-select');
    if (stickyVariantSelect) stickyVariantSelect.value = String(variant.id);

    if (stickyPrice) {
      const compare = stickyPrice.querySelector('.product-atc-sticky__compare');
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        if (compare) {
          compare.textContent = formatMoney(variant.compare_at_price);
          compare.hidden = false;
        } else {
          const s = document.createElement('s');
          s.className = 'product-atc-sticky__compare';
          s.textContent = formatMoney(variant.compare_at_price);
          stickyPrice.prepend(s);
        }
      } else if (compare) {
        compare.hidden = true;
      }
    }

    if (stickySubmit) {
      stickySubmit.disabled = !variant.available;
      stickySubmit.dataset.variantAvailable = String(variant.available);
    }
  }

  // --- Notify form ---

  #setupNotifyForm() {
    const notifyForm = this.ref('notify-form');
    if (!notifyForm) return;

    const submitBtn = this.ref('notify-submit');
    const emailInput = this.ref('notify-email');

    submitBtn?.addEventListener('click', () => this.#handleNotifySubmit(), { signal: this.signal });
    emailInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.#handleNotifySubmit();
      }
    }, { signal: this.signal });
  }

  #toggleNotify(variant) {
    const atcWrapper = this.ref('atc-wrapper');
    const notifyWrapper = this.ref('notify-wrapper');
    if (!notifyWrapper) return;

    const soldOut = !variant.available;
    if (atcWrapper) atcWrapper.hidden = soldOut;
    notifyWrapper.hidden = !soldOut;

    // Update variant ID in notify form
    const notifyVariantInput = this.ref('notify-variant-id');
    if (notifyVariantInput) notifyVariantInput.value = variant.id;
  }

  async #handleNotifySubmit() {
    const email = this.ref('notify-email')?.value;
    if (!email) return;

    const variantId = this.ref('notify-variant-id')?.value;
    const btn = this.ref('notify-submit');
    const form = this.ref('notify-form');
    if (btn) btn.setAttribute('aria-busy', 'true');

    try {
      await fetch(`${window.Shopify?.routes?.root || '/'}contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          'form_type': 'customer',
          'contact[email]': email,
          'contact[tags]': 'back-in-stock',
          'contact[note]': `Variant: ${variantId}`,
        }),
      });

      const success = this.ref('notify-success');
      if (success) success.hidden = false;
      if (form) form.hidden = true;
    } catch {
      this.announce('An error occurred', 'assertive');
    } finally {
      if (btn) btn.removeAttribute('aria-busy');
    }
  }

  teardown() {
    this.#stickyObserver?.disconnect();
  }

}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}
